import React, { useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";

// helpers
import uploadApi from "../../utils/api/upload";
import SuccessModal from "../Shared/SuccessModal/SuccessModal";
import {
  UploadFileValidation,
  UploadFilesValidation,
  FileSizeValidation,
  FileTypeValidation,
  ImageDimensionValidation,
} from "../../utils/file";
import { LoadingIcon } from "@stormwater-software/lib-ui";
import projectInspectionTemplatesApi from "../../utils/api/projectInspectionTemplates";

const uploadFileValidation = UploadFileValidation({
  fileSizeValidation: FileSizeValidation(),
  fileTypeValidation: FileTypeValidation(),
  imageDimensionValidation: ImageDimensionValidation(),
});
const uploadFilesValidation = UploadFilesValidation({
  uploadFileValidation,
});

/**
 * UI for upload dnd input
 */
function UploadInput({
  maxFileSize = 125000000,
  allowedTypes,
  onUpload,
  className,
  showHelpText,
  imageDimensions,
  projectId,
  autoSelectPrimaryTemplate = false,
  inspectionId = null,
  inspectionTemplateTypeId = null,
}) {
  const [showError, setShowError] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  async function onDrop(files) {
    setIsLoading(true);

    const errors = await uploadFilesValidation.validate(files, {
      maxFileSize,
      allowedTypes,
      imageDimensions,
    });

    if (errors.length) {
      setErrorMessages(errors.map(error => error.message));
      setShowError(true);
      setIsLoading(false);

      return;
    }

    await processFiles(files);
    setIsLoading(false);
  }

  async function processFiles(files) {
    try {
      return await Promise.all(files.map(processFile));
    } catch (ex) {
      setErrorMessages([ex]);
      setShowError(true);
    }
  }

  async function processFile(file) {
    if(navigator.onLine){
      let response = await uploadApi.create(file);

      // Determine which template type ID to use
      let templateTypeIdToUse = null;

      // If inspectionTemplateTypeId is explicitly provided (from inspection context), use it
      if (inspectionTemplateTypeId) {
        templateTypeIdToUse = inspectionTemplateTypeId;
      }
      // If inspectionId is provided (accessed via /map URL), fetch the inspection's template
      else if (inspectionId && projectId) {
        try {
          const templatesResponse = await projectInspectionTemplatesApi.index(projectId);
          // Find the template that matches the inspection context
          // For now, we'll use the primary template as fallback
          const primaryTemplate = (templatesResponse.data || []).find(template => template.is_primary);
          
          if (primaryTemplate && primaryTemplate.inspection_template) {
            templateTypeIdToUse = primaryTemplate.inspection_template.inspection_type_id;
          }
        } catch (error) {
          console.error('Error fetching template for inspection:', error);
        }
      }
      // If autoSelectPrimaryTemplate is true (accessed via /active-site-map), use primary template
      else if (autoSelectPrimaryTemplate && projectId) {
        try {
          const templatesResponse = await projectInspectionTemplatesApi.index(projectId);
          const primaryTemplate = (templatesResponse.data || []).find(template => template.is_primary);
          
          if (primaryTemplate && primaryTemplate.inspection_template) {
            templateTypeIdToUse = primaryTemplate.inspection_template.inspection_type_id;
          }
        } catch (error) {
          console.error('Error fetching primary template:', error);
        }
      }

      // Pass the template type ID to the onUpload callback
      if (templateTypeIdToUse) {
        onUpload(response, templateTypeIdToUse);
      } else {
        onUpload(response);
      }
    } else { 
      onUpload(file);
    }
  }

  return (
    <>
      <Dropzone onDrop={onDrop} multiple>
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div
              {...getRootProps()}
              className={classNames("upload-control dropzone", className, {
                "upload-control--active": isDragActive,
                "upload-control--loading": isLoading,
              })}
            >
              <input {...getInputProps()} />
              {isLoading ? (
                <LoadingIcon className={"upload-control__icon"} />
              ) : (
                <i className="fa fa-cloud-upload upload-control__icon" />
              )}
              {showHelpText && !isLoading ? (
                <span>Drag and drop files here to upload.</span>
              ) : null}

              {showHelpText && isLoading ? <span>Uploading files</span> : null}
            </div>
          );
        }}
      </Dropzone>

      <SuccessModal
        show={showError}
        handleClose={() => setShowError(false)}
        handleSubmit={() => setShowError(false)}
        submitBtnText="OK"
        messages={errorMessages}
        title="Error"
        isAlert
      />
    </>
  );
}

UploadInput.propTypes = {
  onUpload: PropTypes.func,
  allowedTypes: PropTypes.arrayOf(PropTypes.string), // array of mime_types
  showHelpText: PropTypes.bool,
  imageDimensions: PropTypes.object,
  projectId: PropTypes.number,
  autoSelectPrimaryTemplate: PropTypes.bool,
  inspectionId: PropTypes.number,
  inspectionTemplateTypeId: PropTypes.number,
};

export default UploadInput;
